import { inject, Injectable, InjectionToken } from '@angular/core';
import { SharedAppSettings, SharedAppSettingsService } from '@luggagehero/shared/app-settings/data-access';
import { Config } from '@luggagehero/shared/environment';
import {
  ADVANCE_BOOKING_DISCOUNT_MODIFIER_KEY,
  BookingAddOns,
  CANCELLATION_FEE_PRODUCT_KEY,
  DISCOUNT_PRODUCT_KEY,
  DISCOUNT_TRANSFORMER_SERVICE,
  DiscountTransformerService,
  EncryptedString,
  GUEST_DAILY_RATE_PRODUCT_KEY,
  GUEST_HOURLY_RATE_PRODUCT_KEY,
  GUEST_INSURANCE_FEE_PRODUCT_KEY,
  GUEST_PREMIUM_INSURANCE_FEE_PRODUCT_KEY,
  GUEST_SERVICE_FEE_PRODUCT_KEY,
  HONEYMOON_REDUCE_RATES_MODIFIER_KEY,
  IDiscount,
  ILuggage,
  INSURANCE_PRODUCT_KEY,
  INSURANCE_PRODUCT_KEYS_BY_CONTEXT,
  IPrice,
  ITimePeriod,
  LegacyOrder,
  LegacyOrderLine,
  MULTI_BAG_REDUCE_STORAGE_RATES_MODIFIER_KEY,
  MULTI_DAY_REDUCE_STORAGE_RATES_MODIFIER_KEY,
  Order,
  OrderLine,
  OrderRequest,
  PREMIUM_INSURANCE_PRODUCT_KEY,
  PRICING_SERVICE,
  PricingModel,
  PricingService,
  ProductList,
  SAME_DAY_STORAGE_DAY_PRODUCT_KEY,
  SAME_DAY_STORAGE_HOUR_PRODUCT_KEY,
  SERVICE_FEE_PRODUCT_KEY,
  STORAGE_DAY_PRODUCT_KEY,
  STORAGE_HOUR_PRODUCT_KEY,
  TIP_PRODUCT_KEY,
  WALK_IN_DAILY_RATE_PRODUCT_KEY,
  WALK_IN_HOURLY_RATE_PRODUCT_KEY,
  WALK_IN_INSURANCE_FEE_PRODUCT_KEY,
  WALK_IN_PREMIUM_INSURANCE_FEE_PRODUCT_KEY,
  WALK_IN_SERVICE_FEE_PRODUCT_KEY,
} from '@luggagehero/shared/interfaces';
import { SharedHttpService } from '@luggagehero/shared/services/http';
import { SharedPromoCodeService } from '@luggagehero/shared/services/promo-codes';
import { SharedStorageService } from '@luggagehero/shared/services/storage';
import { SharedTranslateService } from '@luggagehero/shared/services/translation';
import {
  cloneDeep,
  hasPricedCancellationFee,
  SharedUtilDate,
  SharedUtilList,
  SharedUtilProduct,
  SharedUtilString,
} from '@luggagehero/shared/util';
import { firstValueFrom, map, Observable, switchMap } from 'rxjs';

// Order lines are are sorted according to these priorities
const ORDER_LINE_PRIORITY_STORAGE = 0;
const ORDER_LINE_PRIORITY_OTHER = 1;
const ORDER_LINE_PRIORITY_INSURANCE = 20;
const ORDER_LINE_PRIORITY_FREE_CANCELLATION = 20;
const ORDER_LINE_PRIORITY_PROMO_DISCOUNT = 90;
const ORDER_LINE_PRIORITY_TIPS = 100;

export const ORDER_SERVICE_ENDPOINT_TOKEN = new InjectionToken<string>('ORDER_SERVICE_ENDPOINT_TOKEN');

@Injectable({
  providedIn: 'root',
})
export class SharedOrderService {
  private ordersEndpoint = inject(ORDER_SERVICE_ENDPOINT_TOKEN);
  private http = inject(SharedHttpService);
  private promoCodeService = inject(SharedPromoCodeService);
  private translateService = inject(SharedTranslateService);
  private storageService = inject(SharedStorageService);
  private appSettingsService = inject(SharedAppSettingsService);

  // Translation keys needed by the service
  private text = {
    STORAGE_PAY_AS_YOU_GO: '',
    PRICE_STORAGE: '',
    INSURANCE: '',
    BASIC_INSURANCE: '',
    PREMIUM_INSURANCE: '',
    DAY: '',
    DAYS: '',
    HOUR: '',
    HOURS: '',
    YOU_ARE_SAVING_X_ON_ADDITIONAL_DAYS_PART_1: '',
    CANCELLATION_FEE_EXPLANATION_PART_1: '',
    CANCELLATION_FEE_EXPLANATION_PART_2: '',
    GROUP_DISCOUNT_APPLIES: '',
    PER_HOUR_SHORT: '',
  };

  private _latestQuote: LegacyOrder;
  private _latestProductList: ProductList;

  protected discountTransformerService = inject<DiscountTransformerService>(DISCOUNT_TRANSFORMER_SERVICE);
  private pricingService = inject<PricingService>(PRICING_SERVICE);

  public get latestQuote(): LegacyOrder {
    return this._latestQuote;
  }

  public get latestProductList(): ProductList {
    return this._latestProductList;
  }

  private get settings(): SharedAppSettings {
    return this.appSettingsService.current;
  }

  public clear() {
    this._latestQuote = null;
    this._latestProductList = null;
  }

  public async requestCancellation(bookingId: string): Promise<LegacyOrder> {
    const url = `${Config.environment.TRAVELER_API}/v3/bookings/${bookingId}/request_cancel`;
    const res = await this.http.post<Order | EncryptedString>(url, null);

    const newOrder = SharedUtilString.tryDecryptAndParseString(res, Config.environment.CRYPTO_KEY);
    this._latestQuote = await this.convertNewOrder(newOrder, false, false);

    return this._latestQuote;
  }

  public getRequestForQuote(
    storageLocationId: string,
    pricingModel: PricingModel,
    luggage: ILuggage,
    period: ITimePeriod,
    addOns: BookingAddOns,
    optionalProducts: string[],
    discountCode?: string,
  ): OrderRequest {
    const normalBags = typeof luggage?.normal === 'number' ? luggage.normal : 1;
    const handBags = typeof luggage?.hand === 'number' ? luggage.hand : 0;

    const requestForQuote: OrderRequest = {
      storageLocationId,
      from: SharedUtilDate.serializeDate(period.from),
      to: SharedUtilDate.serializeDate(period.to),
      bags: normalBags + handBags,
      pricingModel,
      addOns: { ...addOns },
      optionalProducts,
      discountCode,
      // This service renders a same-day storage line like its advance twin (AM-4961).
      supportSameDayProducts: true,
    };

    if (this.storageService.variant) {
      if (this.storageService.variant === this.settings.EXPERIMENT_VARIANTS.homelessEntrepreneur) {
        addOns.insurance = false;
      }
      requestForQuote.variantId = this.storageService.variant;
    }

    return requestForQuote;
  }

  /**
   * Generates a preliminary order based on the specified pricing and booking paramters. Used in order summary views
   * before creating a booking and for bookings that are not yet completed.
   */
  public async generateQuote(
    storageLocationId: string,
    pricingModel: PricingModel,
    luggage: ILuggage,
    period: ITimePeriod,
    addOns: BookingAddOns,
    optionalProducts: string[],
    discountCode?: string,
  ): Promise<LegacyOrder>;
  public async generateQuote(requestForQuote: OrderRequest): Promise<LegacyOrder>;
  public async generateQuote(
    requestForQuoteOrStorageLocationId: OrderRequest | string,
    pricingModel?: PricingModel,
    luggage?: ILuggage,
    period?: ITimePeriod,
    addOns?: BookingAddOns,
    optionalProducts?: string[],
    discountCode?: string,
  ): Promise<LegacyOrder> {
    let requestForQuote: OrderRequest;

    if (typeof requestForQuoteOrStorageLocationId === 'string') {
      const storageLocationId = requestForQuoteOrStorageLocationId;
      requestForQuote = this.getRequestForQuote(
        storageLocationId,
        pricingModel,
        luggage,
        period,
        addOns,
        optionalProducts,
        discountCode,
      );
    } else {
      requestForQuote = requestForQuoteOrStorageLocationId;
    }

    // TODO: Remove when API is updated to handle this
    delete requestForQuote.addOns.loyaltyMember;
    console.log('requestForQuote', requestForQuote);

    const url = `${this.ordersEndpoint}/request_quote`;
    const res = await this.http.post<Order | EncryptedString>(url, requestForQuote);

    const newOrder = SharedUtilString.tryDecryptAndParseString(res, Config.environment.CRYPTO_KEY);
    const quote = await this.convertNewOrder(newOrder, false, false);

    this._latestQuote = quote;

    const appSettings = await firstValueFrom(this.appSettingsService.$current);

    // Set default selection for optional products
    newOrder.productList.products.forEach((p) => {
      if (appSettings.OPTIONAL_PRODUCTS_SELECTED_BY_DEFAULT?.includes(p.key)) {
        p.selected = true;
      }
    });
    this._latestProductList = newOrder.productList;

    if (!Config.isProduction) {
      console.debug(SharedUtilString.formatOrderEvent(this._latestQuote, 'generated'));
    }

    return quote;
  }

  public calculateTotal(orderLines: LegacyOrderLine[]): number {
    let total = 0;

    for (const line of orderLines) {
      if (line.isPricing) {
        continue; // Skip pricing lines as they don't reflect a final price
      }

      if (line.optional && !line.selected) {
        continue; // Skip optional lines that are not selected
      }

      const quantity = typeof line.quantity === 'number' ? line.quantity : 1;
      total += line.total ?? quantity * line.unitPrice;
    }
    return total;
  }

  public calculateHoursCharged(order: LegacyOrder, totalAmountCharged: number): number {
    if (order.pricingModel !== 'hourly') {
      return 0;
    }

    let storageCharged = totalAmountCharged;
    let hourlyOrderLine: LegacyOrderLine;

    // Deduct all non-storage fees from the validation charge amount
    order.orderLines.forEach((ol) => {
      if (SharedUtilProduct.isHourlyStorageFee(ol.product)) {
        hourlyOrderLine = ol;
      } else if (!ol.optional || ol.selected) {
        storageCharged -= ol.unitPrice * ol.quantity;
      }
    });

    if (!hourlyOrderLine || storageCharged <= 0) {
      return 0;
    }

    const storagePerHour = Math.round(hourlyOrderLine.unitPrice * hourlyOrderLine.quantity * 100) / 100;
    const storageTotal = Math.round(storageCharged * 100) / 100;
    const numberOfHoursCharged = Math.round(storageTotal / storagePerHour);

    if (numberOfHoursCharged < 1) {
      return 0;
    }

    return numberOfHoursCharged;
  }

  public getHourlyPaymentValidationNote(order: LegacyOrder, validationAmount: number): string {
    const defaultHours = this.appSettingsService.current.defaultHoursForPaymentValidation || 2;
    const numberOfHours = this.calculateHoursCharged(order, validationAmount) || defaultHours;

    const part1 = this.translateService.instant('RESERVE_FIRST_X_HOURS_PART_1').trim();
    const part2 = this.translateService.instant('RESERVE_FIRST_X_HOURS_PART_2').trim();

    const hour = this.translateService.instant('HOUR');
    const hours = this.translateService.instant('HOURS');

    const hourSuffix = numberOfHours === 1 ? hour : hours;

    return `* ${part1} ${numberOfHours} ${hourSuffix} ${part2}`;
  }

  public getHourlyPaymentValidationNote$(order: LegacyOrder, validationAmount: number): Observable<string> {
    return this.appSettingsService.$current.pipe(
      map((appSettings) => {
        const defaultHours = appSettings.defaultHoursForPaymentValidation || 2;
        return this.calculateHoursCharged(order, validationAmount) || defaultHours;
      }),
      switchMap((numberOfHours) => {
        const translationKeys = ['RESERVE_FIRST_X_HOURS_PART_1', 'RESERVE_FIRST_X_HOURS_PART_2', 'HOUR', 'HOURS'];
        return this.translateService.get(translationKeys).pipe(
          map((translatedText) => {
            const part1 = translatedText['RESERVE_FIRST_X_HOURS_PART_1'].trim();
            const part2 = translatedText['RESERVE_FIRST_X_HOURS_PART_2'].trim();
            const hourSuffix = numberOfHours === 1 ? translatedText['HOUR'] : translatedText['HOURS'];
            return `* ${part1} ${numberOfHours} ${hourSuffix} ${part2}`;
          }),
        );
      }),
    );
  }

  /**
   * @param options.countUnselectedInsurance count an insurance line the traveler was offered but did
   *   not take (`optional && !selected`, e.g. the placeholder `convertNewOrder` adds). That was the
   *   behaviour before AM-5017 and overstates an uninsured booking; it is kept only so the booking
   *   service can hold the analytics value steady until the AM-5017 switch is turned on.
   */
  public getEstimatedPrice(order: LegacyOrder, options: { countUnselectedInsurance?: boolean } = {}): IPrice {
    const avgStorageHours = 5;
    let storage: number;

    // Same-day lines (AM-4961) are estimated like their advance twins — the API's `estimateOrderStorage`
    // carries the same keys, so the conversion value reported here and server-side still agree.
    const dailyStorageLine =
      order.orderLines.find((ol) => ol.product._id === STORAGE_DAY_PRODUCT_KEY) ||
      order.orderLines.find((ol) => ol.product._id === SAME_DAY_STORAGE_DAY_PRODUCT_KEY) ||
      order.orderLines.find((ol) => ol.product._id === WALK_IN_DAILY_RATE_PRODUCT_KEY);

    if (dailyStorageLine) {
      storage = Math.round(dailyStorageLine.unitPrice * dailyStorageLine.quantity * 100) / 100;
    } else {
      const hourlyStorageLine =
        order.orderLines.find((ol) => ol.product._id === STORAGE_HOUR_PRODUCT_KEY) ||
        order.orderLines.find((ol) => ol.product._id === SAME_DAY_STORAGE_HOUR_PRODUCT_KEY) ||
        order.orderLines.find((ol) => ol.product._id === WALK_IN_HOURLY_RATE_PRODUCT_KEY);

      if (hourlyStorageLine) {
        storage = Math.round(hourlyStorageLine.unitPrice * hourlyStorageLine.quantity * avgStorageHours * 100) / 100;
      }
    }

    const serviceFee =
      this.calculateOrderLineTotal(SERVICE_FEE_PRODUCT_KEY, order) ||
      this.calculateOrderLineTotal(GUEST_SERVICE_FEE_PRODUCT_KEY, order) ||
      this.calculateOrderLineTotal(WALK_IN_SERVICE_FEE_PRODUCT_KEY, order);
    // Whichever insurance product the order carries — any flow's, and Basic *or* Premium (AM-4345).
    // Reading the lines rather than probing one key per flow means a new tier can't silently drop out
    // of the estimate. (It also drops a copy-paste that read the *guest service fee* as the guest
    // insurance fee.) At most one insurance line exists: `convertNewOrder` only synthesises the
    // deselected one when the order has none.
    //
    // That deselected line is an offer, not a charge, so it is skipped like `calculateTotal` skips it
    // (AM-5017): counting it sent bags × the insurance price as value for every uninsured booking.
    const startupFee = order.orderLines
      .filter((ol) => SharedUtilProduct.isInsuranceFee(ol.product))
      .filter((ol) => options.countUnselectedInsurance || !ol.optional || ol.selected)
      .reduce((total, ol) => total + this.calculateOrderLineTotal(ol.product._id, order), 0);

    const discount = this.calculateOrderLineTotal(DISCOUNT_PRODUCT_KEY, order);
    const total = storage + serviceFee + startupFee + discount;

    const estimatedPrice: IPrice = {
      currency: order.currency,
      storage,
      serviceFee,
      startupFee,
      discount,
      total,
    };
    if (!Config.isProduction) {
      console.log(`Estimated price: ${estimatedPrice.currency} ${estimatedPrice.total}`, estimatedPrice);
    }
    return estimatedPrice;
  }

  public async convertNewOrder(newOrder: Order, isCancelled: boolean, isFinal: boolean): Promise<LegacyOrder> {
    if (!Config.isProduction) {
      console.log(`Server-side order (${newOrder.orderRequest.pricingModel})`, newOrder);
    }

    const clonedOrder = cloneDeep(newOrder);

    // Remove zero value discount
    clonedOrder.orderLines = clonedOrder.orderLines.filter(
      (ol) => ol.productKey !== DISCOUNT_PRODUCT_KEY || ol.finalPrice !== 0,
    );

    const appSettings = await firstValueFrom(this.appSettingsService.$current);

    const isCancellation = clonedOrder.orderLines.find((ol) => ol.productKey === CANCELLATION_FEE_PRODUCT_KEY)
      ? true
      : false;

    // For cancellation orders, remove all order lines with zero units (effectively leaving only the cancellation fee)
    if (isCancellation && appSettings.IS_ORDER_SUMMARY_HIDE_CANCELED_ORDER_LINES_ENABLED) {
      clonedOrder.orderLines = clonedOrder.orderLines.filter(
        (ol) => ol.productKey === CANCELLATION_FEE_PRODUCT_KEY || ol.units > 0,
      );
    }

    this.text = await firstValueFrom(this.translateService.get(this.text));

    // Convert all the order lines received from the server to the client side format
    const orderLines: LegacyOrderLine[] = [];
    await Promise.all(
      clonedOrder.orderLines.map(async (ol) => {
        const res = await this.convertNewOrderLine(ol, clonedOrder, isCancellation, isFinal, appSettings);
        if (res) {
          orderLines.push(...res);
        }
      }),
    );

    if (
      !isCancellation &&
      // Any tier of any flow's insurance counts as "insurance is on the order" (AM-4345 added the
      // premium tiers, so enumerating keys here was one edit away from being wrong).
      !orderLines.find((ol) => SharedUtilProduct.isInsuranceFee(ol.product)) &&
      this.storageService.variant !== appSettings.EXPERIMENT_VARIANTS.homelessEntrepreneur
    ) {
      // Add order line to show deselected insurance product
      SharedUtilList.appendIfNotNullOrUndefined(orderLines, this.createInsuranceOrderLine(clonedOrder));
    }

    if (
      !isCancellation &&
      !clonedOrder.orderLines.find((ol) => ol.productKey === DISCOUNT_PRODUCT_KEY) &&
      clonedOrder.orderRequest.discountCode
    ) {
      // No discount on the order received by the server but there is a discount code added
      SharedUtilList.appendIfNotNullOrUndefined(orderLines, await this.createDiscountOrderLine(clonedOrder));
    }

    SharedUtilList.appendIfNotNullOrUndefined(
      orderLines,
      this.createFreeCancellationOrderLine(clonedOrder, isCancelled),
    );

    // Create the client side order
    const order: LegacyOrder = {
      _id: clonedOrder._id,
      orderRequest: clonedOrder.orderRequest,
      pricingModel: clonedOrder.orderRequest.pricingModel,
      bags: clonedOrder.orderRequest.bags,
      bagsModified: clonedOrder.orderRequest.bagsModified ?? clonedOrder.orderRequest.bags,
      minMaxBagsPolicy: clonedOrder.orderRequest.minMaxBagsPolicy ?? false,
      currency: clonedOrder.productList.currency,
      tip: clonedOrder.tip,
      orderLines: orderLines.sort((a, b) => this.compareOrderLines(a, b)),
      productList: clonedOrder.productList,
      total: this.calculateTotal(orderLines),
      createdAt: clonedOrder.createdAt,
      updatedAt: clonedOrder.updatedAt,
      // A true instant (AM-4788) — parsed as such, never through the wall-clock period deserializer.
      freeUntil: clonedOrder.freeUntil ? new Date(clonedOrder.freeUntil) : undefined,
      freeUntilBasis: clonedOrder.freeUntilBasis,
      timezone: clonedOrder.timezone,
    };

    if (!Config.isProduction) {
      console.log(`Client-side order (${order.pricingModel})`, order);
    }

    return order;
  }

  public compareOrderLines(a: LegacyOrderLine, b: LegacyOrderLine): number {
    if (a.product._id === DISCOUNT_PRODUCT_KEY) {
      // Always put discount at the bottom
      return 1;
    }
    if (typeof a.priority === 'number' && typeof b.priority === 'number') {
      // Both order lines have a priority set, use the difference in priority to prioritize them
      return a.priority - b.priority;
    }
    if (typeof a.priority === 'number') {
      // First order line has a priority set, the second doesn't, so prioritize the first
      return -1;
    }
    if (typeof b.priority === 'number') {
      // Second order line has a priority set, the first doesn't, so prioritize the second
      return 1;
    }
    if (a.optional && !b.optional) {
      // The first order line is optional, the second isn't, so prioritize the second
      return -1;
    }
    if (!a.optional && b.optional) {
      // The first order line is not optional, the second is, so prioritize the first
      return 1;
    }
    // Consider the order lines of equal priority
    return 0;
  }

  public async convertNewOrderLine(
    value: OrderLine,
    order: Order,
    isCancellation: boolean,
    isFinal: boolean,
    appSettings: SharedAppSettings,
  ): Promise<LegacyOrderLine[]> {
    const product = order.productList.products.find((p) => p.key === value.productKey);
    const numberOfBagsModified = order.orderRequest.bagsModified ?? order.orderRequest.bags;
    const numberOfBags = order.orderRequest.bags;

    const isDropOff = [
      WALK_IN_DAILY_RATE_PRODUCT_KEY,
      WALK_IN_HOURLY_RATE_PRODUCT_KEY,
      GUEST_DAILY_RATE_PRODUCT_KEY,
      GUEST_HOURLY_RATE_PRODUCT_KEY,
    ].includes(value.productKey);

    // TODO: Once transitioned to fully encrypted orders the separate decryption of modifified price can be removed
    const modifiedPrice = SharedUtilString.tryDecryptAndParseString(value.modifiedPrice, Config.environment.CRYPTO_KEY);

    const orderLine: LegacyOrderLine = {
      isPricing: false,
      product: {
        _id: value.productKey,
        name: value.name,
      },
      quantity: value.units,
      unitPrice: modifiedPrice?.length > 0 ? modifiedPrice[modifiedPrice.length - 1].unitPrice : value.unitPrice,
      showQuantity: false,
      showUnitPrice: false,
    };

    if (isCancellation) {
      orderLine.selected = value.units > 0;
    }

    const orderLines = [orderLine];

    const storageSavingsModifierKeys: string[] = [];
    let savingsOrderLineUnitPriceMultiplier = 1;
    let showStorageSavings = true;

    // HACK: Until we have placeholder functionality in the tags, we are hardcoding most product info here
    switch (value.productKey) {
      // A same-day line (AM-4961) renders exactly like its advance twin, marketplace min-bags included.
      case WALK_IN_DAILY_RATE_PRODUCT_KEY:
      case GUEST_DAILY_RATE_PRODUCT_KEY:
      case SAME_DAY_STORAGE_DAY_PRODUCT_KEY:
      case STORAGE_DAY_PRODUCT_KEY: {
        const storageLabel = this.text.PRICE_STORAGE;
        const numberOfDays = value.units > 0 ? value.units / numberOfBagsModified : 0;
        const daysLabel = (numberOfDays === 1 ? this.text.DAY : this.text.DAYS).toLowerCase();

        // If a max bags policy is applied, we need to cap the chargeable bags at numberOfBagsModified
        const applyCap = numberOfBagsModified !== numberOfBags;

        // if a guest booking then minBags should be 1
        const minBagsEnabled = isDropOff
          ? appSettings.IS_DROP_OFF_MIN_BAGS_ENABLED
          : appSettings.IS_BOOKING_MIN_BAGS_ENABLED;
        const minBags = isDropOff ? appSettings.DROP_OFF_MIN_BAGS : appSettings.BOOKING_MIN_BAGS;

        orderLine.product.name = `${storageLabel}, ${numberOfDays} ${daysLabel}`;
        orderLine.product.unitSingular = 'BAG';
        orderLine.product.unitPlural = 'BAGS';
        orderLine.showQuantity = true;
        orderLine.showQuantityAsUpTo = minBagsEnabled && numberOfBags <= minBags ? true : false;
        orderLine.priority = ORDER_LINE_PRIORITY_STORAGE;

        if (orderLine.showQuantityAsUpTo && numberOfBags <= minBags) {
          orderLine.product.info = this.minBagsInfo(minBags);
        }

        if (isCancellation && orderLine.quantity === 0) {
          orderLine.optional = true;
        }

        // HACK: For daily bookings, we get e.g. 6 units if the traveler stored 2 bags for 3 days
        orderLine.quantity = numberOfBags;
        orderLine.unitPrice *= numberOfDays;
        // Keep the multiplier recoverable so callers can get back to a per-day rate (AM-4637).
        orderLine.storagePeriods = numberOfDays;

        if (applyCap) {
          // When a cap is applied, we set an explicit total on order line so calculation uses the capped bag count
          orderLine.total = Math.round(orderLine.unitPrice * numberOfBagsModified * 100) / 100;

          // IMPORTANT: When total is set we must NOT add separate storage savings lines to avoid double-discount.
          showStorageSavings = false;
        }

        savingsOrderLineUnitPriceMultiplier = numberOfDays;

        storageSavingsModifierKeys.push(
          MULTI_BAG_REDUCE_STORAGE_RATES_MODIFIER_KEY,
          MULTI_DAY_REDUCE_STORAGE_RATES_MODIFIER_KEY,
          HONEYMOON_REDUCE_RATES_MODIFIER_KEY,
          ADVANCE_BOOKING_DISCOUNT_MODIFIER_KEY,
        );

        break;
      }
      case WALK_IN_HOURLY_RATE_PRODUCT_KEY:
      case GUEST_HOURLY_RATE_PRODUCT_KEY:
      case SAME_DAY_STORAGE_HOUR_PRODUCT_KEY:
      case STORAGE_HOUR_PRODUCT_KEY: {
        const isPricing = !isFinal && value.units === 0;
        const storageLabel = this.text.PRICE_STORAGE;
        const numberOfHours = value.units > 0 ? value.units / numberOfBags : 1;
        const hoursLabel = (numberOfHours === 1 ? this.text.HOUR : this.text.HOURS).toLowerCase();
        const periodLabel = `${numberOfHours} ${hoursLabel}`;

        const minBagsEnabled = isDropOff
          ? appSettings.IS_DROP_OFF_MIN_BAGS_ENABLED
          : appSettings.IS_BOOKING_MIN_BAGS_ENABLED;
        const minBags = isDropOff ? appSettings.DROP_OFF_MIN_BAGS : appSettings.BOOKING_MIN_BAGS;

        // One whole key for the pay-as-you-go line (AM-5034): gluing PRICE_STORAGE to a lower-cased
        // PAY_AS_YOU_GO broke languages that capitalise nouns, and showed English wherever the
        // fragment was untranslated.
        orderLine.product.name = isPricing ? this.text.STORAGE_PAY_AS_YOU_GO : `${storageLabel}, ${periodLabel}`;
        orderLine.product.unitSingular = 'BAG';
        orderLine.product.unitPlural = 'BAGS';
        orderLine.showQuantity = !isFinal || value.finalPrice > 0;
        orderLine.showQuantityAsUpTo = minBagsEnabled && numberOfBags <= minBags ? true : false;
        orderLine.priority = ORDER_LINE_PRIORITY_STORAGE;

        if (orderLine.showQuantityAsUpTo && numberOfBags <= minBags) {
          orderLine.product.info = this.minBagsInfo(minBags);
        }

        if (isCancellation && orderLine.quantity === 0) {
          orderLine.optional = true;
        }

        if (isPricing) {
          // For hourly bookings that are not completed, we show the hourly rate as pricing
          orderLine.quantity = numberOfBags;
          orderLine.isPricing = true;
          orderLine.pricingSuffix = this.text.PER_HOUR_SHORT;
          // unitPrice is left as the raw hourly rate here, so it already *is* the per-period rate.
          orderLine.storagePeriods = 1;
        } else {
          // HACK: For completed hourly bookings, we get e.g. 6 units if the traveler stored 2 bags for 3 hours
          orderLine.quantity = value.units > 0 ? value.units / numberOfHours : 0;
          orderLine.unitPrice *= numberOfHours;
          orderLine.storagePeriods = numberOfHours;

          savingsOrderLineUnitPriceMultiplier = numberOfHours;
        }

        storageSavingsModifierKeys.push(
          MULTI_BAG_REDUCE_STORAGE_RATES_MODIFIER_KEY,
          MULTI_DAY_REDUCE_STORAGE_RATES_MODIFIER_KEY,
          HONEYMOON_REDUCE_RATES_MODIFIER_KEY,
          ADVANCE_BOOKING_DISCOUNT_MODIFIER_KEY,
        );

        break;
      }
      case WALK_IN_INSURANCE_FEE_PRODUCT_KEY:
      case WALK_IN_PREMIUM_INSURANCE_FEE_PRODUCT_KEY:
      case GUEST_INSURANCE_FEE_PRODUCT_KEY:
      case GUEST_PREMIUM_INSURANCE_FEE_PRODUCT_KEY:
      case PREMIUM_INSURANCE_PRODUCT_KEY:
      case INSURANCE_PRODUCT_KEY: {
        if (!product) {
          // No insurance product found, so we don't show insurance on the order summary
          return null;
        }
        const coverageAmount = this.pricingService.format(product.value, order.productList.currency, 2, true);
        // Named after the product **on the line** — i.e. what was actually charged — not after the
        // requested `addOns.insuranceKey`. The server resolves a tier within its own sales context and
        // can legitimately fall back to Basic (a key from another context, or Premium the shop's context
        // doesn't price), so trusting the request would label a Basic charge, at a Basic price, with a
        // Basic coverage amount, as "Premium Insurance" — the traveler underwritten at one tier while
        // LuggageHero's own payment screen documents another.
        const isPremium = SharedUtilProduct.isPremiumInsuranceFee(product);

        // Both tiers by name (AM-5036): a bare "Insurance" for Basic read as a different product from the
        // drop-off wizard's "Basic insurance".
        orderLine.product.name = isPremium ? this.text.PREMIUM_INSURANCE : this.text.BASIC_INSURANCE;
        orderLine.product.info = {
          header: orderLine.product.name,
          body: this.coveragePerBag(coverageAmount),
        };
        orderLine.optional = true;
        // If the server side order includes insurance as an order line, it means insurance was selected
        orderLine.selected = isCancellation ? orderLine.quantity > 0 : true;
        orderLine.priority = ORDER_LINE_PRIORITY_INSURANCE;

        break;
      }
      case DISCOUNT_PRODUCT_KEY: {
        const discount = await this.promoCodeService.checkPromoCode(order.orderRequest.discountCode);

        orderLine.product.name = 'PRICE_DISCOUNT';
        orderLine.product.info = {
          header: order.orderRequest.discountCode,
          body: this.discountTransformerService.transform(discount, numberOfBags, false),
        };
        orderLine.priority = ORDER_LINE_PRIORITY_PROMO_DISCOUNT;

        break;
      }
      case TIP_PRODUCT_KEY: {
        orderLine.product.name = product?.tags.find((t) => t.key === 'displayName')?.value || 'PRICE_TIP';
        orderLine.priority = ORDER_LINE_PRIORITY_TIPS;

        break;
      }
      case CANCELLATION_FEE_PRODUCT_KEY: {
        if (isCancellation && orderLine.quantity === 0) {
          // Only show cancellation fee on order summary when one or more units are actually included in the order
          return null;
        }

        orderLine.product.name = 'CANCELLATION_FEE';
        orderLine.product.unitSingular = 'BAG';
        orderLine.product.unitPlural = 'BAGS';
        orderLine.showQuantity = true;
        orderLine.showUnitPrice = true;

        if (orderLine.unitPrice > 0) {
          // The fee applies past the pinned free-cancellation deadline (AM-4786), which is not
          // "same day": a late cancellation, whatever day it falls on.
          const bodyPart1 = this.text.CANCELLATION_FEE_EXPLANATION_PART_1;
          const bodyPart2 = this.translateService.instant('CANCELLATION_FEE_EXPLANATION_DEADLINE');
          orderLine.product.info = {
            header: 'LATE_CANCELLATIONS',
            body: `${bodyPart1}<br><br>${bodyPart2}`,
          };
        }
        break;
      }
      default: {
        // Fall back to order line name if we don't find a product with display name and other product info
        orderLine.product.name = product
          ? product.tags?.find((t) => t.key === 'displayName')?.value || product.name
          : value.name;

        const header = product?.tags?.find((t) => t.key === 'infoHeader')?.value;
        const body = product?.tags?.find((t) => t.key === 'infoBody')?.value;

        if (body) {
          orderLine.product.info = { header, body };
        }
        orderLine.priority = ORDER_LINE_PRIORITY_OTHER;

        break;
      }
    }

    if (showStorageSavings && storageSavingsModifierKeys.length > 0) {
      modifiedPrice?.forEach((modifier) => {
        // Check if this modification is a known discount on storage that we want to display explicitly to the user
        // Get the savings from the modifier and calculate savings per order line unit
        const modifierSavings = -modifier.priceDelta;
        // Only a real saving gets its own line: with none (or a negative one) there is nothing to show, and
        // the percentage below would divide by a zero original price.
        if (storageSavingsModifierKeys.includes(modifier.priceModifierKey) && modifierSavings > 0) {
          const savingsPerUnit = modifierSavings * savingsOrderLineUnitPriceMultiplier;

          // Add savings to the original order line as we will deduct them on a separate line
          orderLine.unitPrice += savingsPerUnit;

          const isHoneymoon = modifier.priceModifierKey === HONEYMOON_REDUCE_RATES_MODIFIER_KEY;
          const percentageSavings = Math.round((modifierSavings / (modifier.unitPrice + modifierSavings)) * 100);
          const youAreSaving = this.text.YOU_ARE_SAVING_X_ON_ADDITIONAL_DAYS_PART_1;

          // Add separate order line representing the savings
          orderLines.push({
            product: {
              name: modifier.priceModifierKey,
              info: isHoneymoon
                ? {
                    header: 'NEW_STORAGE_LOCATION',
                    body: 'HONEYMOON_ORDER_LINE_TOOLTIP_BODY',
                  }
                : {
                    header: 'DISCOUNT_APPLIED',
                    body: `${youAreSaving} ${percentageSavings}%`,
                  },
            },
            priority: orderLine.priority + 1, // Place immediately after original order line
            unitPrice: -savingsPerUnit,
            quantity: orderLine.quantity,
            isPricing: orderLine.isPricing,
            pricingSuffix: orderLine.pricingSuffix,
            // Same period multiplier as the storage line, so the per-period rate nets out (AM-4961).
            storagePeriods: orderLine.storagePeriods,
            // The storage line's own id — the field the rate helpers match on — not a value it was built from.
            discountsStorageProductKey: orderLine.product._id,
          });
        }
      });
    }

    return orderLines;
  }

  private calculateOrderLineTotal(productId: string, order: LegacyOrder): number {
    const orderLine = order.orderLines.find((ol) => ol.product._id === productId);
    return orderLine ? (orderLine.total ?? Math.round(orderLine.unitPrice * orderLine.quantity * 100) / 100) : 0;
  }

  /**
   * The "Free cancellation" summary line (AM-4788). It states the order's own pinned deadline —
   * "Free cancellation until 9:00am, Sep 14", at the storage location — and only while that
   * deadline still lies ahead; once it has passed, or when the order carries no deadline at all,
   * there is no line rather than a promise that has stopped being true. A deadline the 15-minute
   * post-booking grace period set (`freeUntilBasis === 'grace'`) is told as "within 15 minutes of
   * booking", never as an instant: the grace period is a safety net, not a time to watch. The one
   * exception is a cancel quote the server has already priced at zero (`isFreeCancellationOrder`):
   * that *is* free, whatever the deadline says (incident, fail-open), and keeps the plain label.
   *
   * "Still ahead" compares instants, never wall clocks: the pre-2026-03 version compared the device's
   * end-of-day with the arrival date in the device's zone and was wrong by the traveler's offset.
   */
  private createFreeCancellationOrderLine(order: Order, isCancelled = false): LegacyOrderLine {
    const isWalkIn = order.orderRequest.addOns?.walkIn === true;
    const isFreeCancellationOrder =
      order.orderLines.find((ol) => ol.productKey === CANCELLATION_FEE_PRODUCT_KEY) && order.total === 0 ? true : false;

    const isPaidCancellationOrder =
      order.orderLines.find((ol) => ol.productKey === CANCELLATION_FEE_PRODUCT_KEY) && order.total > 0 ? true : false;

    const freeUntil = order.freeUntil ? new Date(order.freeUntil) : null;
    const isDeadlineAhead = !!freeUntil && !isNaN(freeUntil.getTime()) && Date.now() < freeUntil.getTime();

    if (!Config.isProduction) {
      console.log(`Cancellation debug info`, {
        isFreeCancellationOrder,
        isWalkIn,
        isCancelled,
        freeUntil,
        isDeadlineAhead,
      });
    }

    if (isPaidCancellationOrder || (isWalkIn && !isFreeCancellationOrder)) {
      return null;
    }

    //
    // Round 8: the price row is the flag. Where this booking's PINNED list carries no priced
    // CANCELLATION_FEE, nothing is charged for cancelling late, so the summary shows exactly the
    // line master showed — same name, same info header and body, returned unconditionally.
    // Returning `null` here would delete a line the traveler sees today, which is itself a visible
    // change and the thing this round exists to avoid.
    //
    if (!hasPricedCancellationFee(order.productList?.products)) {
      return {
        isPricing: false,
        product: {
          _id: 'freeCancellation',
          name: 'FREE_CANCELLATION',
          info: { header: 'FREE_CANCELLATION', body: 'CANCEL_UNTIL_MIDNIGHT_EXPLANATION' },
        },
        quantity: 1,
        unitPrice: 0,
        selected: !isWalkIn || isFreeCancellationOrder,
        priority: ORDER_LINE_PRIORITY_FREE_CANCELLATION,
      };
    }

    const isGrace = order.freeUntilBasis === 'grace';
    const deadline =
      isDeadlineAhead && !isGrace
        ? SharedUtilDate.formatFreeCancellationDeadline(
            freeUntil,
            order.timezone,
            this.translateService.currentLanguage,
          )
        : '';
    const promise: 'grace' | 'dated' | null = isDeadlineAhead && isGrace ? 'grace' : deadline ? 'dated' : null;

    // Nothing to promise (no deadline, passed, or no timezone to render a dated one in) means no
    // line — unless the server has already priced this cancel quote at zero, where the plain label
    // is true.
    if (!isFreeCancellationOrder && !promise) {
      return null;
    }

    const name =
      promise === 'grace'
        ? 'FREE_CANCELLATION_WITHIN_GRACE'
        : promise === 'dated'
          ? this.translateService.instant('FREE_CANCELLATION_UNTIL', undefined, { deadline })
          : 'FREE_CANCELLATION';
    const info =
      promise === 'grace'
        ? { header: 'FREE_CANCELLATION_WITHIN_GRACE', body: 'FREE_CANCELLATION_WITHIN_GRACE_EXPLANATION' }
        : promise === 'dated'
          ? {
              header: name,
              body: this.translateService.instant('FREE_CANCELLATION_UNTIL_EXPLANATION', undefined, { deadline }),
            }
          : undefined;

    //
    // A cancel QUOTE is exactly an order carrying a CANCELLATION_FEE order line — the same signal
    // the caller reads as `isCancellation`. That is the cancel dialog, where the deadline is the
    // thing being acted on, so the row stays visible there.
    //
    // Everywhere else (booking panel, wizard Confirm, manage-booking) the row is now for assistive
    // technology only: the panel states the policy once in "More info", but that is a tooltip a
    // keyboard or screen-reader user cannot open, so removing the line outright would leave them
    // with no deadline at all. See `visuallyHidden` on LegacyOrderLine.
    //
    // Only reached on the priced path — the unpriced branch returned above, unchanged.
    //
    const isCancelQuote = !!order.orderLines.find((ol) => ol.productKey === CANCELLATION_FEE_PRODUCT_KEY);

    const orderLine: LegacyOrderLine = {
      isPricing: false,
      product: {
        _id: 'freeCancellation',
        name,
        info,
      },
      quantity: 1,
      unitPrice: 0,
      selected: !isWalkIn || isFreeCancellationOrder,
      priority: ORDER_LINE_PRIORITY_FREE_CANCELLATION,
      visuallyHidden: !isCancelQuote,
    };

    return orderLine;
  }

  /**
   * The insurance product an order's add-ons point at: its flow's Premium when that is what was
   * requested, else its flow's Basic.
   *
   * Deliberately resolved **within the flow's own product pair** — a requested key belonging to
   * another flow (or nothing at all) yields this flow's Basic, never a foreign product. Mirrors the
   * server's `getInsuranceRateKeyFromOrderRequest`, which is the authority on what was charged.
   */
  private getInsuranceKey(addOns: { insurance: boolean; insuranceKey?: string; walkIn?: boolean; guest?: boolean }) {
    const { basic, premium } = INSURANCE_PRODUCT_KEYS_BY_CONTEXT[SharedUtilProduct.insuranceContext(addOns)];
    return addOns.insuranceKey === premium ? premium : basic;
  }

  /**
   * The min-bags note on a storage line, as whole sentences with the count in them (AM-5036). It was
   * glued from fragments with "bags" lower-cased, which no translation could make read right in German
   * (nouns are capitalised) or follow another language's word order.
   */
  private minBagsInfo(minBags: number): { header: string; body: string } {
    return {
      header: this.translateService.instant('STORE_UP_TO_X_BAGS_AT_THIS_RATE', undefined, { count: minBags }),
      body: this.translateService.instant('STORAGE_LOCATION_MIN_X_BAGS_COUNT', undefined, { count: minBags }),
    };
  }

  /** "€3,000 coverage per bag", one sentence with the amount in it (AM-5036), not three glued fragments. */
  private coveragePerBag(amount: string): string {
    return this.translateService.instant('COVERAGE_X_AMOUNT_PER_BAG', undefined, { amount });
  }

  private createInsuranceOrderLine(order: Order): LegacyOrderLine {
    const insuranceProduct = order.productList.products.find(
      (p) => p.key === this.getInsuranceKey(order.orderRequest.addOns),
    );

    if (!insuranceProduct) {
      // This shouldn't happen

      console.log('returning empty insurance order line');
      return null;
    }

    const coverageAmount = this.pricingService.format(insuranceProduct.value, order.productList.currency, 2, true);

    const orderLine: LegacyOrderLine = {
      isPricing: false,
      product: {
        _id: insuranceProduct.key,
        name: 'INSURANCE',
        info: {
          header: this.text.INSURANCE,
          body: this.coveragePerBag(coverageAmount),
        },
      },
      quantity: order.orderRequest.bags,
      unitPrice: insuranceProduct.cost,
      optional: true,
      selected: false,
      showQuantity: false,
      showUnitPrice: false,
      priority: ORDER_LINE_PRIORITY_INSURANCE,
    };

    return orderLine;
  }

  private async createDiscountOrderLine(order: Order): Promise<LegacyOrderLine> {
    const discount = await this.promoCodeService.checkPromoCode(order.orderRequest.discountCode);
    const numberOfBags = order.orderRequest.bags;

    const discountValue = this.getDiscountValue(order, discount);

    if (!discountValue) {
      return null;
    }

    const orderLine: LegacyOrderLine = {
      product: {
        _id: DISCOUNT_PRODUCT_KEY,
        name: 'PRICE_DISCOUNT',
        info: {
          header: discount.code,
          body: this.discountTransformerService.transform(discount, numberOfBags, false),
        },
      },
      unitPrice: -(discountValue / numberOfBags),
      quantity: numberOfBags,
      isRelative: order.orderRequest.pricingModel === 'hourly' && discount.type === 'percentage',
      isPricing: order.orderRequest.pricingModel === 'hourly' && discount.type === 'percentage',
      pricingSuffix: discount.type === 'percentage' ? '%' : null,
      priority: ORDER_LINE_PRIORITY_PROMO_DISCOUNT,
    };

    const appSettings = await firstValueFrom(this.appSettingsService.$current);

    switch (discount.code) {
      case appSettings.DEFAULT_AUTO_GROUP_DISCOUNT_CODE:
        orderLine.product.info.body += `. ${this.text.GROUP_DISCOUNT_APPLIES}.`;
        break;
    }

    return orderLine;
  }

  private getDiscountValue(order: Order, discount: IDiscount): number {
    const numberOfBags = order.orderRequest.bags;
    const maxBagsForDiscount = discount.maxBags || numberOfBags;

    if (order.finalised || order.orderRequest.pricingModel === 'daily') {
      const discountFromOrderLines =
        order.orderLines.find((ol) => ol.productKey === DISCOUNT_PRODUCT_KEY)?.finalPrice || 0;

      return discountFromOrderLines;
    }

    let discountValue = discount.value;

    if (discount.type === 'hours') {
      const hourlyRateProductKeys: string[] = [
        STORAGE_HOUR_PRODUCT_KEY,
        SAME_DAY_STORAGE_HOUR_PRODUCT_KEY,
        WALK_IN_HOURLY_RATE_PRODUCT_KEY,
        GUEST_HOURLY_RATE_PRODUCT_KEY,
      ];

      const hourlyRate = order.orderLines.find((p) => hourlyRateProductKeys.includes(p.productKey))?.unitPrice || null;

      if (hourlyRate) {
        discountValue = discount.value * hourlyRate * Math.min(maxBagsForDiscount, numberOfBags);
      }
    }

    return discountValue;
  }
}
